// CP-142: single runtime source of truth for Affiliate/Book on the frontend.
//
// Every surface (Detail BookNowButton, Hot Deals, Best Deals, Home deal
// shelves, Compare, footer commission line) reads affiliateState.enabled.
// The value comes from GET /api/config -> { affiliate_enabled }. It stays
// false until the backend says exactly `true`, and any fetch error, timeout or
// malformed payload keeps it false, so the OFF state is the safe default.
//
// CP-141: affiliateState.lines lists the cruise lines CruiseDirect sells
// (/api/config -> affiliate_lines). A CTA for any other line stays hidden:
// use canBookLine(enabled, line).
import { reactive } from 'vue';
import api from '@/services/api';

export const affiliateState = reactive({ enabled: false, loaded: false, lines: [] });

// Strict parse: only a literal boolean true turns the flag on.
export function parseAffiliateEnabled(payload) {
  return Boolean(payload) && typeof payload === 'object' && payload.affiliate_enabled === true;
}

// Lines CruiseDirect sells. Anything malformed means "no lines".
export function parseAffiliateLines(payload) {
  if (!payload || typeof payload !== 'object' || !Array.isArray(payload.affiliate_lines)) return [];
  return payload.affiliate_lines.filter((line) => typeof line === 'string' && line.trim() !== '');
}

function normLine(line) {
  return typeof line === 'string' ? line.trim().replace(/\s+/g, ' ').toLowerCase() : '';
}

// True when CruiseDirect sells this line (per the loaded config).
export function sellsLine(line, lines = affiliateState.lines) {
  const key = normLine(line);
  return key !== '' && Array.isArray(lines) && lines.some((l) => normLine(l) === key);
}

// A Book/partner CTA renders only when the flag is on AND the line is sold.
export function canBookLine(enabled, line, lines = affiliateState.lines) {
  return enabled === true && sellsLine(line, lines);
}

// CP-212: render-driven footer commission line (CTO rule: the line shows whenever any affiliate link
// is rendered on the page, and not when none is).
//
// Every affiliate anchor (CruiseDirect CTAs, Viator tour links) carries `v-affiliate-link`. The
// directive counts the element while it is mounted, so the count is exactly "affiliate links on
// the page right now": v-if, route changes and data loads are all handled by Vue mounting and
// unmounting the element. A source test requires the directive on every rel="...sponsored" anchor.
export const affiliateLinkTracker = reactive({ rendered: 0 });

export const affiliateLink = {
  mounted() {
    affiliateLinkTracker.rendered += 1;
  },
  unmounted() {
    affiliateLinkTracker.rendered = Math.max(0, affiliateLinkTracker.rendered - 1);
  },
};

// Footer commission line: only while at least one affiliate link is on screen.
export function showCommissionFooter(rendered = affiliateLinkTracker.rendered) {
  return Number(rendered) > 0;
}

let pending = null;

export function loadAffiliateFlag() {
  if (pending) return pending;
  pending = Promise.resolve()
    .then(() => api.getSiteConfig())
    .then((res) => {
      affiliateState.enabled = parseAffiliateEnabled(res && res.data);
      affiliateState.lines = affiliateState.enabled ? parseAffiliateLines(res && res.data) : [];
    })
    .catch(() => {
      affiliateState.enabled = false;
      affiliateState.lines = [];
    })
    .finally(() => {
      affiliateState.loaded = true;
    });
  return pending;
}
